import { createBigDecimal, minus, plus, times } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { FeeSubCategory, Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { getCostByAssetBalancesAndBorrows } from './util/getCostByAssetBalancesAndBorrows'
import { updateCostBy5PercentRule } from './util/updateCostBy5PercentRule'

type CreateFeeTransactionParameters = CreateCategoryTransactionParameters & {
  readonly subCategory?: FeeSubCategory
  readonly transfer: Transfer
  readonly costBasis: CostBasis
  readonly use5PercentRule?: boolean
}

type CreateFeeTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createFeeTransaction = ({
  actionId,
  timestamp,
  subCategory,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  costBasis,
  use5PercentRule = false,
}: CreateFeeTransactionParameters): CreateFeeTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = transfer.price ?? createBigDecimal(0n)
  const value = times(transfer.amount, price, 6)

  // Get cost by asset balance & asset borrow
  const {
    cost,
    assetBalances: keyAssetBalances,
    assetBorrows: keyAssetBorrows,
    usedBalance,
  } = transfer.asset.type === 'FiatCurrency'
    ? {
        cost: value,
        assetBalances: [],
        assetBorrows: [],
        usedBalance: {
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
        },
      }
    : getCostByAssetBalancesAndBorrows({
        amount: transfer.amount,
        assetBalances: assetBalances.get(key) ?? [],
        assetBorrows: assetBorrows.get(key) ?? [],
        usedBalance: usedBalances.get(key) ?? {
          amount: createBigDecimal(0n),
          value: createBigDecimal(0n),
        },
        costBasis,
      })
  if (keyAssetBalances.length > 0) {
    assetBalances.set(key, keyAssetBalances)
  } else {
    assetBalances.delete(key)
  }
  if (keyAssetBorrows.length > 0) {
    assetBorrows.set(key, keyAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }
  if (usedBalance.amount.value !== 0n) {
    usedBalances.set(key, usedBalance)
  } else {
    usedBalances.delete(key)
  }

  const costWith5PercentRule = use5PercentRule
    ? updateCostBy5PercentRule(cost, value)
    : cost
  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const transaction: Transaction = {
    actionId,
    timestamp,
    order: 0,
    category: 'fee',
    subCategory,
    ins: [],
    outs: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost,
        balance,
        borrowing,
        feePl:
          subCategory === 'realize-loss'
            ? createBigDecimal(0n)
            : times(value, createBigDecimal(-1n), 6),
        incomePl: createBigDecimal(0n),
        tradePl: minus(value, costWith5PercentRule),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl:
          subCategory === 'realize-loss'
            ? times(value, createBigDecimal(-1n), 6)
            : createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: times(costWith5PercentRule, createBigDecimal(-1n), 6),
      },
    ],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
  }
}
